'use client';

import { memo, useState } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import {
  CreditCardIcon,
  DebtConsolidationIcon,
} from '@/assets/icons/LoanReasonIcons';
import NextButton from '@/components/Button/NextButton';
import { FormData, LoanReasons } from '@/types/form';
import { fieldRules } from '@/utils/form';
import { WarningIcon } from '@/assets/icons/WarningIcon';
import OptionButtons from '../OptionButtons/OptionButtons';
import generalStyles from '../FormBody.module.scss';
import useModal from '@/hooks/useModal';
import styles from './StepThree.module.scss';

interface Props {
  formData?: FormData;
  register?: UseFormRegister<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  isModal?: boolean;
  verificationError?: boolean;
}

const LoanReasonForm = ({
  formData,
  register,
  handleSubmit,
  control,
  setValue,
  trigger,
  isModal = false,
  verificationError = false,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const [selectedValue, setSelectedValue] = useState(formData?.loan_reason);
  const handleSelect = (value: LoanReasons) => {
    setSelectedValue(value);
    setValue?.('loan_reason', value);
    trigger?.('loan_reason');
  };

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit?.();
    if (!errors?.loan_reason) {
      closeModal();
    }
  };

  const selectStep = () => {
    if (!isModal) {
      handleSubmit?.();
    }
  };

  return (
    <form onSubmit={submitForm} className={styles.form}>
      <div className={generalStyles.variantsWrapper}>
        <input
          type="text"
          className={generalStyles.hiddenInput}
          id="loan_reason"
          {...register?.('loan_reason', fieldRules.loan_reason)}
        />
        <OptionButtons
          text={LoanReasons.debt_consolidation}
          selected={selectedValue === LoanReasons.debt_consolidation}
          icon={<DebtConsolidationIcon />}
          onClick={() => {
            handleSelect(LoanReasons.debt_consolidation);
            selectStep();
          }}
        />
        <OptionButtons
          text={LoanReasons.credit_card_refinance}
          selected={selectedValue === LoanReasons.credit_card_refinance}
          icon={<CreditCardIcon />}
          onClick={() => {
            handleSelect(LoanReasons.credit_card_refinance);
            selectStep();
          }}
        />
        {errors?.loan_reason && (
          <span className={generalStyles.error}>
            {errors.loan_reason.message}
          </span>
        )}
      </div>
      {isModal && (
        <NextButton
          buttonType="submit"
          type="button"
          text="Apply changes"
          styles={`${generalStyles.button} ${isModal ? generalStyles.smallButton : ''}`}
          isArrow={!isModal}
        />
      )}
      {verificationError && (
        <p
          className={`${generalStyles.error} ${generalStyles.verificationError}`}
        >
          <WarningIcon className={generalStyles.warningIcon} />
          <span>We couldn&apos;t verify that you are human</span>
        </p>
      )}
    </form>
  );
};

export default memo(LoanReasonForm);
